/**
 * POS light sync — catalog pull + ordered outbox drain — plan/28, plan/33.
 */

import {
  bindPosTerminal,
  closePosDrawer,
  fetchPosCatalog,
  getPosSession,
  openPosDrawer,
  sellPosTicket,
  voidPosTicket,
  type PosCatalogItem,
  type PosSession,
} from "@/shared/api/pos";
import { postReturn } from "@/shared/api/returns";
import { apiErrorCode } from "@/shared/api/api-error";
import {
  conflictCount,
  enqueueOutbox,
  listConflictOutbox,
  loadCatalogCache,
  loadSessionCache,
  loadTicketReturnCache,
  pendingCount,
  removeOutbox,
  removeTicketReturnCache,
  saveCatalogCache,
  saveSessionCache,
  saveTicketReturnCache,
  listOutbox,
  updateOutbox,
  type CatalogCache,
  type TicketReturnCache,
} from "./idb";
import { rotateDeviceInstallId } from "./device-install-id";
import { activeOnly, mergeById as mergeByIdShared } from "@icraftx/shared/v1";
import {
  applyLocalStockDelta,
  buildFullReturnPayload,
  findPendingReturn,
  findPendingSell,
  isIdempotentSyncError,
  isLikelyNetworkError,
  isSoftConfirmableCode,
  mergeById,
  offlineReturnBlockReason,
  outboxIsDrainable,
  patchPayloadForSoftConfirm,
  reverseLocalStockDelta,
  sortOutboxForDrain,
  syncChipState,
  type OutboxItem,
  type OutboxKind,
  type ReturnOutboxPayload,
  type Watermark,
} from "./sync-math";
import {
  mergeNumberRange,
  type TerminalNumberRange,
} from "./number-range-math";
import {
  pullParties,
  pullPayMethodsSync,
  type SyncParty,
  type SyncPayMethod,
} from "@/shared/api/sync";
import {
  loadPartiesCache,
  loadPayMethodsCache,
  savePartiesCache,
  savePayMethodsCache,
} from "./idb";

export { applyLocalStockDelta };

function normalizeRange(
  range: PosSession["numberRange"],
): TerminalNumberRange | null {
  if (!range) return null;
  return {
    nextSeq: range.nextSeq,
    rangeEnd: range.rangeEnd,
    prefix: range.prefix,
    padWidth: range.padWidth ?? 4,
  };
}

export type SyncSnapshot = {
  online: boolean;
  pendingCount: number;
  chip: ReturnType<typeof syncChipState>["chip"];
  label: string;
  catalog: PosCatalogItem[];
  catalogUpdated: string | null;
  session: PosSession | null;
};

function watermarkFromCatalog(
  items: PosCatalogItem[],
  responseLastUpdated: string | null,
): Watermark {
  if (!responseLastUpdated) return { lastUpdated: null, lastId: null };
  const same = items
    .filter((i) => {
      const iso =
        typeof i.updatedAt === "string"
          ? i.updatedAt
          : new Date(i.updatedAt).toISOString();
      return iso === responseLastUpdated || iso.startsWith(responseLastUpdated);
    })
    .map((i) => i.id)
    .sort();
  const lastId = same[same.length - 1] ?? null;
  return { lastUpdated: responseLastUpdated, lastId };
}

export async function pullCatalog(
  companyRef: string,
  terminalId: string,
  opts?: { forceFull?: boolean },
): Promise<CatalogCache> {
  const cached = await loadCatalogCache(companyRef, terminalId);
  const useSince =
    !opts?.forceFull && cached?.watermark.lastUpdated
      ? cached.watermark
      : null;

  const res = await fetchPosCatalog(companyRef, terminalId, {
    since: useSince?.lastUpdated ?? undefined,
    sinceId: useSince?.lastId ?? undefined,
  });

  const merged = useSince
    ? mergeById(cached?.items ?? [], res.data)
    : res.data;
  const items = activeOnly(merged);

  const watermark = watermarkFromCatalog(
    useSince ? res.data : items,
    res.lastUpdated ?? useSince?.lastUpdated ?? null,
  );

  const cache: CatalogCache = {
    companyRef,
    terminalId,
    items,
    watermark: watermark.lastUpdated
      ? watermark
      : (cached?.watermark ?? { lastUpdated: null, lastId: null }),
    savedAt: new Date().toISOString(),
  };
  await saveCatalogCache(cache);
  return cache;
}

export async function pullPartiesChannel(
  companyRef: string,
  opts?: { forceFull?: boolean },
): Promise<{
  parties: SyncParty[];
  watermark: Watermark;
}> {
  const cached = await loadPartiesCache(companyRef);
  const useSince =
    !opts?.forceFull && cached?.watermark.lastUpdated
      ? cached.watermark
      : null;
  const res = await pullParties(companyRef, {
    since: useSince?.lastUpdated ?? undefined,
    sinceId: useSince?.lastId ?? undefined,
  });
  const merged = useSince
    ? mergeByIdShared(cached?.parties ?? [], res.data)
    : res.data;
  const parties = activeOnly(
    merged.map((p) => ({
      ...p,
      isCustomer: Boolean(p.isCustomer),
      isSupplier: Boolean(p.isSupplier),
    })),
  );
  const watermark: Watermark = {
    lastUpdated: res.lastUpdated,
    lastId: res.lastId,
  };
  await savePartiesCache({
    companyRef,
    parties,
    watermark: watermark.lastUpdated
      ? watermark
      : (cached?.watermark ?? { lastUpdated: null, lastId: null }),
    savedAt: new Date().toISOString(),
  });
  return { parties, watermark };
}

export async function pullPayMethodsChannel(
  companyRef: string,
  opts?: { forceFull?: boolean },
): Promise<{
  methods: SyncPayMethod[];
  watermark: Watermark;
}> {
  const cached = await loadPayMethodsCache(companyRef);
  const useSince =
    !opts?.forceFull && cached?.watermark.lastUpdated
      ? cached.watermark
      : null;
  const res = await pullPayMethodsSync(companyRef, {
    since: useSince?.lastUpdated ?? undefined,
    sinceId: useSince?.lastId ?? undefined,
  });
  const merged = useSince
    ? mergeByIdShared(cached?.methods ?? [], res.data)
    : res.data;
  const methods = activeOnly(merged);
  const watermark: Watermark = {
    lastUpdated: res.lastUpdated,
    lastId: res.lastId,
  };
  await savePayMethodsCache({
    companyRef,
    methods,
    watermark: watermark.lastUpdated
      ? watermark
      : (cached?.watermark ?? { lastUpdated: null, lastId: null }),
    savedAt: new Date().toISOString(),
  });
  return { methods, watermark };
}

export async function hydratePos(
  companyRef: string,
  terminalId: string,
  online: boolean,
): Promise<{
  session: PosSession;
  catalog: PosCatalogItem[];
  catalogUpdated: string | null;
  fromCache: boolean;
}> {
  if (!online) {
    const [sessCache, catCache] = await Promise.all([
      loadSessionCache(companyRef, terminalId),
      loadCatalogCache(companyRef, terminalId),
    ]);
    if (!sessCache?.session) {
      throw new Error("Offline — open this register once while online first");
    }
    return {
      session: sessCache.session,
      catalog: catCache?.items ?? [],
      catalogUpdated: catCache?.watermark.lastUpdated ?? null,
      fromCache: true,
    };
  }

  try {
    try {
      await bindPosTerminal(companyRef, terminalId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (!/did not match the expected pattern|invalid header/i.test(msg)) {
        throw err;
      }
      rotateDeviceInstallId();
      await bindPosTerminal(companyRef, terminalId);
    }
    const [session, catalog, priorCache] = await Promise.all([
      getPosSession(companyRef, terminalId),
      pullCatalog(companyRef, terminalId),
      loadSessionCache(companyRef, terminalId),
    ]);
    // Masters: parties + pay methods (delta) — plan/33
    await Promise.all([
      pullPartiesChannel(companyRef).catch(() => null),
      pullPayMethodsChannel(companyRef).catch(() => null),
    ]);
    const mergedRange = mergeNumberRange(
      normalizeRange(session.numberRange),
      normalizeRange(priorCache?.session.numberRange ?? null),
    );
    const mergedSession: PosSession = {
      ...session,
      numberRange: mergedRange,
    };
    await saveSessionCache({
      companyRef,
      terminalId,
      session: mergedSession,
      savedAt: new Date().toISOString(),
    });
    return {
      session: mergedSession,
      catalog: catalog.items,
      catalogUpdated: catalog.watermark.lastUpdated,
      fromCache: false,
    };
  } catch (err) {
    if (!isLikelyNetworkError(err)) throw err;
    const [sessCache, catCache] = await Promise.all([
      loadSessionCache(companyRef, terminalId),
      loadCatalogCache(companyRef, terminalId),
    ]);
    if (!sessCache?.session) throw err;
    return {
      session: sessCache.session,
      catalog: catCache?.items ?? [],
      catalogUpdated: catCache?.watermark.lastUpdated ?? null,
      fromCache: true,
    };
  }
}

async function postOutboxItem(
  companyRef: string,
  item: OutboxItem,
): Promise<void> {
  const payload = item.payload as Record<string, unknown>;
  switch (item.kind) {
    case "pos.drawer.open":
      await openPosDrawer(companyRef, item.terminalId, payload as {
        id?: string;
        openingFloat?: string;
        clientCreatedAt?: string;
      });
      return;
    case "pos.drawer.close":
      await closePosDrawer(companyRef, item.terminalId, {
        closingCashCounted: String(
          (payload as { closingCashCounted?: string }).closingCashCounted ??
            "0",
        ),
        confirmVariance: Boolean(
          (payload as { confirmVariance?: boolean }).confirmVariance,
        ),
      });
      return;
    case "pos.sell":
      await sellPosTicket(
        companyRef,
        item.terminalId,
        payload as Parameters<typeof sellPosTicket>[2],
      );
      return;
    case "pos.void": {
      const ticketId = String(payload.ticketId ?? "");
      if (!ticketId) return;
      await voidPosTicket(companyRef, ticketId, {
        fromTerminal: true,
        reason:
          typeof payload.reason === "string" ? payload.reason : "POS void",
        idempotencyKey:
          typeof payload.idempotencyKey === "string"
            ? payload.idempotencyKey
            : `void-${ticketId}`,
        overrideRequestId:
          typeof payload.overrideRequestId === "string"
            ? payload.overrideRequestId
            : undefined,
        ...(payload.offlinePinApprove &&
        typeof payload.offlinePinApprove === "object"
          ? {
              offlinePinApprove: payload.offlinePinApprove as {
                id?: string;
                permissionKey: string;
                approverUserId: string;
                summary?: string;
              },
            }
          : {}),
      });
      return;
    }
    case "pos.return": {
      await postReturn(
        companyRef,
        payload as Parameters<typeof postReturn>[1],
      );
      return;
    }
    default:
      return;
  }
}

export type LocalVoidResult =
  | {
      mode: "dropped";
      lines: Array<{ productId: string; qty: string }>;
    }
  | { mode: "queued" };

/**
 * Void before sync: drop pending sell so it never posts.
 * After sync (no pending sell): queue void for drain — plan/28.
 */
export async function voidLocalOrEnqueue(input: {
  terminalId: string;
  ticketId: string;
  reason?: string;
  overrideRequestId?: string | null;
  offlinePinApprove?: {
    id: string;
    permissionKey: string;
    approverUserId: string;
    summary?: string;
  } | null;
}): Promise<LocalVoidResult> {
  const pending = await listOutbox(input.terminalId);
  const sell = findPendingSell(pending, input.ticketId);
  if (sell) {
    await removeOutbox(sell.id);
    await removeTicketReturnCache(input.ticketId);
    const payload = sell.payload as {
      lines?: Array<{ productId: string; qty: string }>;
    };
    return { mode: "dropped", lines: payload.lines ?? [] };
  }
  await enqueueMutation({
    terminalId: input.terminalId,
    kind: "pos.void",
    id: `void-${input.ticketId}`,
    payload: {
      ticketId: input.ticketId,
      reason: input.reason ?? "POS void",
      idempotencyKey: `void-${input.ticketId}`,
      overrideRequestId: input.overrideRequestId ?? undefined,
      ...(input.offlinePinApprove
        ? { offlinePinApprove: input.offlinePinApprove }
        : {}),
    },
  });
  await removeTicketReturnCache(input.ticketId);
  return { mode: "queued" };
}

/**
 * Queue a full cash return for a synced POS ticket — plan/28.
 * Unsynced sales must void & re-ring instead.
 */
export async function enqueueOfflineReturn(input: {
  terminalId: string;
  ticketId: string;
  number: string;
}): Promise<
  | { ok: true; payload: ReturnOutboxPayload; lines: TicketReturnCache["lines"] }
  | { ok: false; reason: string }
> {
  const pending = await listOutbox(input.terminalId);
  const cache = await loadTicketReturnCache(input.ticketId);
  const block = offlineReturnBlockReason({
    pendingSell: Boolean(findPendingSell(pending, input.ticketId)),
    pendingReturn: Boolean(findPendingReturn(pending, input.ticketId)),
    hasLineCache: Boolean(cache && cache.lines.length > 0),
  });
  if (block) return { ok: false, reason: block };
  const payload = buildFullReturnPayload({
    ticketId: input.ticketId,
    warehouseId: cache!.warehouseId,
    lines: cache!.lines,
    idempotencyKey: `ret-${input.ticketId}`,
  });
  if (payload.lines.length === 0) {
    return { ok: false, reason: "No returnable qty on cached ticket" };
  }
  await enqueueMutation({
    terminalId: input.terminalId,
    kind: "pos.return",
    id: `return-${input.ticketId}`,
    payload,
  });
  return { ok: true, payload, lines: cache!.lines };
}

export async function cacheTicketForReturn(
  cache: TicketReturnCache,
): Promise<void> {
  await saveTicketReturnCache(cache);
}

export {
  reverseLocalStockDelta,
  findPendingSell,
  findPendingReturn,
  offlineReturnBlockReason,
};

export async function drainOutbox(
  companyRef: string,
  terminalId: string,
): Promise<{
  drained: number;
  /** First item that needs cashier attention (soft limit / hard fail). */
  conflict: OutboxItem | null;
  /** Network blip — keep pending, retry later. */
  blocked: OutboxItem | null;
}> {
  const items = sortOutboxForDrain(await listOutbox(terminalId)).filter(
    outboxIsDrainable,
  );
  let drained = 0;
  for (const item of items) {
    try {
      await postOutboxItem(companyRef, item);
      await removeOutbox(item.id);
      drained += 1;
    } catch (err) {
      if (isLikelyNetworkError(err)) {
        return { drained, conflict: null, blocked: item };
      }
      if (isIdempotentSyncError(err)) {
        await removeOutbox(item.id);
        drained += 1;
        continue;
      }
      const code = apiErrorCode(err);
      const msg = err instanceof Error ? err.message : "Sync failed";
      const marked: OutboxItem = {
        ...item,
        syncStatus: "conflict",
        conflictCode: code,
        lastSyncError: msg,
      };
      await updateOutbox(marked);
      return { drained, conflict: marked, blocked: null };
    }
  }
  return { drained, conflict: null, blocked: null };
}

/**
 * Soft-confirm and re-post a conflicted outbox item (oversell / discount /
 * drawer variance). Plan/28 conflict UI — force sync path.
 */
export async function resolveConflictConfirm(
  companyRef: string,
  item: OutboxItem,
): Promise<void> {
  const code = item.conflictCode;
  if (!isSoftConfirmableCode(code)) {
    throw new Error(
      item.lastSyncError ??
        "This sync needs a manager override — confirm won’t clear it",
    );
  }
  const patched: OutboxItem = {
    ...item,
    payload: patchPayloadForSoftConfirm(item.payload, code!),
    syncStatus: "pending",
    conflictCode: null,
    lastSyncError: null,
  };
  await updateOutbox(patched);
  await postOutboxItem(companyRef, patched);
  await removeOutbox(patched.id);
}

/**
 * Drop a conflicted outbox item so the register isn’t stuck.
 * For unsynced sells, caller should reverse local stock — plan/28 void local.
 */
export async function resolveConflictDrop(item: OutboxItem): Promise<{
  kind: OutboxKind;
  sellLines: Array<{ productId: string; qty: string }>;
}> {
  let sellLines: Array<{ productId: string; qty: string }> = [];
  if (item.kind === "pos.sell") {
    const payload = item.payload as {
      lines?: Array<{ productId: string; qty: string }>;
    };
    sellLines = payload.lines ?? [];
  }
  await removeOutbox(item.id);
  return { kind: item.kind, sellLines };
}

export async function runSyncPass(
  companyRef: string,
  terminalId: string,
): Promise<{
  drained: number;
  catalog: CatalogCache | null;
  conflict: OutboxItem | null;
}> {
  const { drained, conflict, blocked } = await drainOutbox(
    companyRef,
    terminalId,
  );
  if (conflict || blocked) {
    return { drained, catalog: null, conflict };
  }
  try {
    const [catalog, session] = await Promise.all([
      pullCatalog(companyRef, terminalId),
      getPosSession(companyRef, terminalId),
    ]);
    await Promise.all([
      pullPartiesChannel(companyRef).catch(() => null),
      pullPayMethodsChannel(companyRef).catch(() => null),
    ]);
    await saveSessionCache({
      companyRef,
      terminalId,
      session,
      savedAt: new Date().toISOString(),
    });
    return { drained, catalog, conflict: null };
  } catch {
    return { drained, catalog: null, conflict: null };
  }
}

export async function enqueueMutation(input: {
  terminalId: string;
  kind: OutboxKind;
  payload: unknown;
  id?: string;
}): Promise<OutboxItem> {
  return enqueueOutbox({
    id: input.id ?? crypto.randomUUID(),
    terminalId: input.terminalId,
    kind: input.kind,
    payload: input.payload,
    createdAt: new Date().toISOString(),
  });
}

export async function syncSnapshot(
  companyRef: string,
  terminalId: string,
  online: boolean,
  session: PosSession | null,
  catalog: PosCatalogItem[],
  catalogUpdated: string | null,
): Promise<SyncSnapshot> {
  const [pending, conflicts] = await Promise.all([
    pendingCount(terminalId),
    conflictCount(terminalId),
  ]);
  const { chip, label } = syncChipState({
    online,
    pendingCount: pending,
    conflictCount: conflicts,
  });
  return {
    online,
    pendingCount: pending,
    chip,
    label,
    catalog,
    catalogUpdated,
    session,
  };
}

export { listConflictOutbox, conflictCount };

